import { useState, memo, useEffect } from "react";
import { Box, TextField, Typography } from "@mui/material";
import { DndContext, DragOverlay } from "@dnd-kit/core";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";

import BszSectionCard from "../BszSectionCard";

import RoomCard from "./RoomCard";
import SortableRoom from "./SortableRoom";

import { useAppContext } from "../../../../../2_ksbHub/context/appContext";
import { useBszFinderContext } from "../../../context/bszContext";
import { isRoomAvailableNow } from "../../../utils/roomAvailability";

/**
 * ✅ SECTIONS CONFIG
 */
const SECTION_CONFIG = [
  { key: "A", label: "Klassenräume A-Trakt", type: "all" },
  { key: "C", label: "Klassenräume C-Trakt", type: "all" },
  { key: "D", label: "Klassenräume D-Trakt", type: "all" },

  { key: "group", label: "Gruppenräume", type: "all" },
  { key: "general", label: "Allgemeine Lernplätze", type: "all" },
  { key: "silent", label: "Stille Lernplätze", type: "all" },
  { key: "support", label: "Office (nur LP angezeigt)", type: "teacher" },
];

function ArbeitsplaetzeSection({
  rooms = [],
  dialogs,
  actions,
  onSelectRoom,
  viewMode,
}) {
  const { isTeacherView, isStudentView, isMobile, isAStudent } =
    useAppContext();
  const { isBszAdmin } = useBszFinderContext();

  const [activeRoom, setActiveRoom] = useState(null);

  const { currentUser } = useAppContext();
  const [now, setNow] = useState(new Date());
  const [roomQuery, setRoomQuery] = useState("");

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 30000); //recalculate every 30sec

    return () => clearInterval(interval);
  }, []);

  const filteredRooms = rooms
    .filter((room) =>
      isRoomAvailableNow(room, now, isBszAdmin, isTeacherView, isStudentView),
    )
    .filter((room) =>
      room.name?.toLowerCase().includes(roomQuery.trim().toLowerCase()),
    )
    .filter((room) => {
      if (viewMode === "all") return true;

      if (viewMode === "free") {
        return (room.free || 0) > 0;
      }

      if (viewMode === "mine") {
        return (
          room.studentSlots?.some(
            (s) => s.reservedBy?.id === currentUser?.id,
          ) ||
          room.teacherSlots?.some((s) => s.reservedBy?.id === currentUser?.id)
        );
      }

      return true;
    });

  /**
   * ✅ DRAG START
   */
  const handleDragStart = ({ active }) => {
    const room = rooms.find((r) => r.id === active.id);
    setActiveRoom(room || null);
  };

  /**
   * ✅ DRAG END
   */
  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) {
      setActiveRoom(null);
      return;
    }

    const dragged = rooms.find((r) => r.id === active.id);
    const target = rooms.find((r) => r.id === over.id);

    if (!dragged || !target) {
      setActiveRoom(null);
      return;
    }

    actions.moveRoom({
      roomId: dragged.id,
      section: target.section,
      row: target.layout_row ?? 0,
      col: target.layout_col ?? 0,
    });

    setActiveRoom(null);
  };
  /**
   * ✅ FILTER SECTIONS
   */
  const visibleSections = SECTION_CONFIG.filter(
    (section) => !isAStudent || section.type !== "teacher",
  );
  /**
   * ✅ GROUP BY SECTION
   */
  const sections = visibleSections.reduce((acc, sec) => {
    acc[sec.key] = [];
    return acc;
  }, {});

  filteredRooms.forEach((room) => {
    const key = room.section || "general";
    if (!sections[key]) sections[key] = [];
    sections[key].push(room);
  });

  /**
   * ✅ SORT
   */
  Object.values(sections).forEach((secRooms) => {
    secRooms.sort((a, b) => {
      if ((a.layout_row ?? 0) !== (b.layout_row ?? 0)) {
        return (a.layout_row ?? 0) - (b.layout_row ?? 0);
      }
      return (a.layout_col ?? 0) - (b.layout_col ?? 0);
    });
  });
  const hasVisibleContent = visibleSections.some(
    ({ key }) => sections[key].length > 0,
  );
  return (
    <BszSectionCard
      title="Arbeitsplätze"
      dialogs={dialogs}
      actions={actions}
      showActions={isBszAdmin && !isTeacherView && !isStudentView}
      defaultExpanded={isMobile ? false : true}
    >
      {hasVisibleContent ? (
        <TextField
          size="small"
          placeholder="Raum suchen…"
          value={roomQuery}
          onChange={(e) => setRoomQuery(e.target.value)}
          sx={{ mt: -1, mb: 2 }}
          fullWidth
        />
      ) : (
        <Typography>
          Die Arbeitsplätze werden erst kurz vor der BSZ Lektion angezeigt.
        </Typography>
      )}
      <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <SortableContext
          // items={rooms.map((r) => r.id)}
          items={filteredRooms.map((r) => r.id)}
          strategy={rectSortingStrategy}
        >
          {visibleSections.map(({ key, label }) => {
            // ✅ hide empty sections
            if (sections[key].length === 0) return null;

            return (
              <Box key={key} sx={{ mb: 3 }}>
                <Box sx={{ fontWeight: 700, mb: 1, opacity: 0.8 }}>{label}</Box>

                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: isMobile ? 1 : 2,
                    p: 1,
                    borderRadius: 2,
                    backgroundColor: "rgba(255,255,255,0.02)",
                  }}
                >
                  {sections[key].map((room) => (
                    <Box key={room.id}>
                      <SortableRoom room={room} disabled={!isBszAdmin}>
                        {({ listeners, attributes }) => (
                          <RoomCard
                            room={room}
                            isDraggable={isBszAdmin}
                            dragProps={{ ...listeners, ...attributes }}
                            onClick={onSelectRoom}
                            onEdit={dialogs.openEditRoom}
                            onDelete={actions.handleDeleteRoom}
                            alwaysGreen={room.section === "support"}
                          />
                        )}
                      </SortableRoom>
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })}
        </SortableContext>

        <DragOverlay>
          {activeRoom ? (
            <Box sx={{ pointerEvents: "none", transform: "scale(1.05)" }}>
              <RoomCard room={activeRoom} isDraggable={false} />
            </Box>
          ) : null}
        </DragOverlay>
      </DndContext>
    </BszSectionCard>
  );
}

export default memo(ArbeitsplaetzeSection);
